import React from 'react';
import { useCustomPageTitle } from '../../hooks/usePageTitle';
import CreditPurchaseForm from '../../components/credits/CreditPurchaseForm';

const PurchaseCredits = () => {
  useCustomPageTitle('Acheter des crédits');

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Formulaire d'achat fonctionnel */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
        <CreditPurchaseForm />
      </div>

      {/* Section témoignages */}
      <TestimonialSection />

      {/* Comparaison détaillée */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
        <h2 className="text-base font-semibold mb-4 text-center">Comparaison détaillée</h2>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left p-4">Fonctionnalité</th>
                <th className="text-center p-4">Crédit Unitaire<br /><span className="text-sm font-normal">19,90€</span></th>
                <th className="text-center p-4">Abonnement<br /><span className="text-sm font-normal">29,90€/mois</span></th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b">
                <td className="p-4">Nombre d'adresses</td>
                <td className="p-4 text-center">1 adresse</td>
                <td className="p-4 text-center font-bold text-orange-600">Illimitées</td>
              </tr>
              <tr className="border-b">
                <td className="p-4">Estimations par adresse</td>
                <td className="p-4 text-center font-bold text-primary">Illimitées</td>
                <td className="p-4 text-center font-bold text-orange-600">Illimitées</td>
              </tr>
              <tr className="border-b">
                <td className="p-4">Validité</td>
                <td className="p-4 text-center">À vie</td>
                <td className="p-4 text-center">Tant que l'abonnement est actif</td>
              </tr>
              <tr className="border-b">
                <td className="p-4">Support client</td>
                <td className="p-4 text-center">Standard</td>
                <td className="p-4 text-center font-bold text-orange-600">Prioritaire</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      {/* FAQ mise à jour */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
        <h2 className="text-base font-semibold mb-4">Questions fréquentes</h2>
        <div className="space-y-4">
          <details className="bg-gray-50 p-5 rounded-lg group cursor-pointer">
            <summary className="font-medium text-sm">Quelle est la différence entre le Crédit Unitaire et l'Abonnement ?</summary>
            <div className="mt-3 text-gray-600 text-sm">
              La seule différence est le nombre d'adresses. Avec un Crédit Unitaire, vous accédez à une adresse à vie avec des estimations illimitées sur cette adresse. Avec l'Abonnement, vous avez un nombre illimité d'adresses.
            </div>
          </details>

          <details className="bg-gray-50 p-5 rounded-lg group cursor-pointer">
            <summary className="font-medium text-sm">Puis-je passer d'un crédit unitaire à l'abonnement ?</summary>
            <div className="mt-3 text-gray-600 text-sm">
              Oui, à tout moment. Vos estimations existantes sont conservées.
            </div>
          </details>

          <details className="bg-gray-50 p-5 rounded-lg group cursor-pointer">
            <summary className="font-medium text-sm">L'abonnement se renouvelle-t-il automatiquement ?</summary>
            <div className="mt-3 text-gray-600 text-sm">
              Oui. Vous pouvez résilier à tout moment depuis votre espace Facturation, l'accès reste actif jusqu'à la fin de la période en cours.
            </div>
          </details>

          <details className="bg-gray-50 p-5 rounded-lg group cursor-pointer">
            <summary className="font-medium text-sm">Que se passe-t-il si j'annule mon abonnement ?</summary>
            <div className="mt-3 text-gray-600 text-sm">
              Vous conservez l'accès jusqu'à la fin de votre période de facturation. Vos estimations restées sont accessibles en lecture.
            </div>
          </details>

          <details className="bg-gray-50 p-5 rounded-lg group cursor-pointer">
            <summary className="font-medium text-sm">Quelle option choisir pour mon cas ?</summary>
            <div className="mt-3 text-gray-600 text-sm">
              Si vous avez un ou deux biens à estimer, le Crédit Unitaire est plus adapté. Si vous gérez plusieurs biens ou êtes professionnel, l'Abonnement est plus rentable dès le deuxième bien.
            </div>
          </details>
        </div>
      </div>
    </div>
  );
};

export const TestimonialSection = () => {
  const testimonials = [
    {
      name: "Marie L., Paris",
      project: "Rénovation appartement",
      savings: "2 400€ économisés",
      quote: "J'avais 3 devis d'artisans avec des écarts énormes. L'estimation SimulReno m'a permis de comprendre lesquels étaient gonflés et de négocier. 2 400€ économisés sur le chantier final."
    },
    {
      name: "Thomas B., Lyon",
      project: "Extension maison",
      savings: "5 800€ économisés",
      quote: "Je ne savais pas par où commencer. J'ai décrit mon projet à SimulReno AI et en 20 minutes j'avais une liste complète et définitive avec les prix. La banque a accepté mon dossier du premier coup."
    },
    {
      name: "Sophie M., Marseille",
      project: "Rénovation complète",
      savings: "3 200€ économisés",
      quote: "Le PDF professionnel a vraiment fait la différence avec ma banque. Et j'ai pu identifier 3 postes surévalués chez mon artisan grâce au détail poste par poste."
    }
  ];

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
      <h2 className="text-base font-semibold mb-4 text-center">
        Ils ont économisé grâce à SimulReno
      </h2>

      <div className="grid md:grid-cols-3 gap-6">
        {testimonials.map((testimonial, index) => (
          <div key={index} className="bg-gray-50 p-4 rounded-lg">
            <div className="mb-3">
              <p className="font-medium text-sm">{testimonial.name}</p>
              <p className="text-xs text-gray-500">{testimonial.project}</p>
            </div>
            <p className="text-sm text-gray-600 italic mb-3">"{testimonial.quote}"</p>
            <span className="inline-block text-xs font-semibold text-green-700 bg-green-100 px-2 py-1 rounded-full">
              {testimonial.savings}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PurchaseCredits;